import { useState, useEffect, useCallback } from 'react';
import { apiClient, API_ENDPOINTS } from '../config/api';
import type {
  TermsContent,
  ActiveTermsResponse,
  AgreeTermsRequest,
  AgreeTermsResponse,
  PurchaseConsentRequest,
  PurchaseConsentResponse,
  MarketingConsentRequest,
  MarketingConsentResponse,
  TermsCheckResponse,
  TermsType,
} from '../types/terms';

// ── 현행 약관 목록 조회 GET /terms/active ─────────────────────────────────
// 서버 응답: ApiResponse<ActiveTermsListResponse> = { result: { terms: [] }, resultType }

export function useActiveTerms() {
  const [terms, setTerms] = useState<TermsContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<ActiveTermsResponse>(API_ENDPOINTS.TERMS.ACTIVE)
      .then((res) => {
        if (!cancelled) setTerms(res.result.terms);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? '약관 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { terms, loading, error };
}



// ── 가입 시 약관 일괄 동의 POST /terms/agree ──────────────────────────────
// 요청: { agreements: [{ termsId: Long, isAgreed: boolean }] }
// 서버 응답: ApiResponse<Unit> = { result: null, resultType }

export function useSubmitTermsAgreement() {
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async (request: AgreeTermsRequest): Promise<void> => {
    setLoading(true);
    try {
      await apiClient.post<AgreeTermsResponse>(API_ENDPOINTS.TERMS.AGREE, request);
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading };
}

// ── 구매 청약철회 확인 기록 POST /terms/agree/purchase ────────────────────
// 요청: { termsId: Long }
// 서버 응답: ApiResponse<Unit> = { result: null, resultType }

export function useSubmitPurchaseConsent() {
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async (request: PurchaseConsentRequest): Promise<void> => {
    setLoading(true);
    try {
      await apiClient.post<PurchaseConsentResponse>(API_ENDPOINTS.TERMS.AGREE_PURCHASE, request);
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading };
}

// ── 마케팅 수신 동의 현황 조회 GET /terms/marketing ──────────────────────
// 서버 응답: ApiResponse<MarketingConsentStatusResponse> = { result: { isAgreed, agreedAt }, resultType }

export function useMarketingConsentStatus() {
  const [isAgreed, setIsAgreed] = useState(false);
  const [agreedAt, setAgreedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get<{ result: { isAgreed: boolean; agreedAt: string | null }; resultType: string }>(
        API_ENDPOINTS.TERMS.MARKETING,
      )
      .then((res) => {
        if (!cancelled) {
          setIsAgreed(res.result.isAgreed);
          setAgreedAt(res.result.agreedAt);
        }
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? '마케팅 동의 현황을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  return { isAgreed, agreedAt, loading, error, refetch };
}

// ── 마케팅 수신 동의 변경 PUT /terms/marketing ───────────────────────────
// 요청: { isAgreed: boolean }
// 서버 응답: ApiResponse<Unit> = { result: null, resultType }

export function useUpdateMarketingConsent() {
  const [loading, setLoading] = useState(false);

  const update = useCallback(async (isAgreed: boolean): Promise<void> => {
    setLoading(true);
    try {
      const req: MarketingConsentRequest = { isAgreed };
      await apiClient.put<MarketingConsentResponse>(API_ENDPOINTS.TERMS.MARKETING, req);
    } finally {
      setLoading(false);
    }
  }, []);

  return { update, loading };
}

// ── 재동의 필요 여부 체크 GET /terms/check ───────────────────────────────
// 서버 응답: ApiResponse<TermsCheckResponse> = { result: { needsReagreement, terms[] }, resultType }

export function useTermsCheck() {
  const [needsReagreement, setNeedsReagreement] = useState(false);
  const [outdatedTerms, setOutdatedTerms] = useState<TermsContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<TermsCheckResponse>(API_ENDPOINTS.TERMS.CHECK)
      .then((res) => {
        if (!cancelled && res.result) {
          setNeedsReagreement(res.result.needsReagreement);
          setOutdatedTerms(res.result.terms);
        }
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? '약관 상태를 확인하지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { needsReagreement, outdatedTerms, loading, error };
}
